/**
 * @flow
 */

import { lockBodyScroll, unlockBodyScroll } from "../../shared/utils/bodyScrollLock";
import { InternalOnlyError } from "../../shared/utils/errors";

type ShowAlertDialogProps = {
  title: string,
  message: string,
  confirmButtonLabel: string,
  confirmButtonDisabled?: boolean,
  onConfirm?: (event: MouseEvent, datasetRest: { [string]: string }) => void,
  cancelButtonLabel?: ?string,
  isDestructive?: boolean,
  disableCloseOnUnderlayClick?: boolean,
  // Arbitrary extra string keys are passed through to onConfirm as datasetRest.
  ...
};

/**
 * Shows a modal alert dialog using the native <dialog> element.
 */
export function showAlertDialog(props: ShowAlertDialogProps) {
  const {
    title,
    message,
    confirmButtonLabel,
    confirmButtonDisabled = false,
    onConfirm,
    cancelButtonLabel,
    isDestructive = false,
    disableCloseOnUnderlayClick = false,
    ...datasetRest
  } = props;
  const { body } = document;
  if (!body) {
    throw new InternalOnlyError("Cannot show alert dialog because body is not available.");
  }

  const previouslyFocusedElement = document.activeElement;

  const dialog = document.createElement("dialog");
  // eslint-disable-next-line i18next/no-literal-string
  dialog.className = "alert-dialog";
  dialog.setAttribute("role", "alertdialog");
  dialog.setAttribute("aria-labelledby", "dialog-title");
  dialog.setAttribute("aria-describedby", "dialog-message");

  // Take any extra data that was in data attributes on the modal triggering button and attach it to the dialog.
  // This data is passed into the callback method, so can be used for logic. The rest props are inexact (unknown
  // value types), so coerce each to a string to build the map the callback expects.
  const datasetStrings: { [string]: string } = {};
  for (const [key, value] of Object.entries(datasetRest)) {
    datasetStrings[key] = String(value);
    // eslint-disable-next-line i18next/no-literal-string
    const dataKey = `data-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
    dialog.setAttribute(dataKey, datasetStrings[key]);
  }

  // eslint-disable-next-line i18next/no-literal-string
  const iconName = isDestructive ? "fa-times-circle" : "fa-exclamation-circle";
  const iconStyle = isDestructive ? "has-text-danger" : "has-text-warning";
  const confirmButtonType = isDestructive ? "is-danger" : "is-primary";
  const finalCancelButtonLabel =
    cancelButtonLabel || window.pgettext("The button to do nothing when an alert is shown.", "Not Now");

  // Render from a static HTML skeleton, then fill in user-supplied strings via
  // textContent / classList.
  /* eslint-disable i18next/no-literal-string */
  dialog.innerHTML = `
    <div class="alert-dialog-content">
      <h1 id="dialog-title" class="is-size-4 has-text-weight-bold mb-4">
        <span class="mr-2"><span class="icon is-small"><i class="fas"></i></span></span>
      </h1>
      <p id="dialog-message" class="mb-1 is-size-6 alert-dialog-message"></p>
      <div class="alert-dialog-buttons is-flex mt-4 is-flex-wrap-wrap is-flex-direction-row is-justify-content-flex-end">
        <div><button type="button" class="button cancel"></button></div>
        <div><button type="button" class="button confirm"></button></div>
      </div>
    </div>
  `;
  /* eslint-enable i18next/no-literal-string */

  const iconEl = getRequiredElement(dialog, "#dialog-title i");
  iconEl.classList.add(iconName, iconStyle);

  const titleEl = getRequiredElement(dialog, "#dialog-title");
  titleEl.appendChild(document.createTextNode(title));

  const messageEl = getRequiredElement(dialog, "#dialog-message");
  messageEl.textContent = message;

  const cancelButton = getRequiredButton(dialog, "button.cancel");
  cancelButton.textContent = finalCancelButtonLabel;

  const confirmButton = getRequiredButton(dialog, "button.confirm");
  confirmButton.textContent = confirmButtonLabel;
  confirmButton.classList.add(confirmButtonType);
  if (confirmButtonDisabled) {
    confirmButton.disabled = true;
  }

  body.appendChild(dialog);

  const closeDialog = (returnValue: string) => {
    dialog.close(returnValue);
  };

  if (!disableCloseOnUnderlayClick) {
    // Track mousedown so a drag that started on dialog content but ended on the backdrop
    // (e.g. selecting text and releasing outside) doesn't close the dialog.
    let mouseDownOnBackdrop = false;
    dialog.addEventListener("mousedown", (event: MouseEvent) => {
      mouseDownOnBackdrop = event.target === dialog;
    });
    dialog.addEventListener("click", (event: MouseEvent) => {
      if (mouseDownOnBackdrop && event.target === dialog) {
        closeDialog("cancel");
      }
      mouseDownOnBackdrop = false;
    });
  }

  cancelButton.addEventListener("click", () => closeDialog("cancel"));

  confirmButton.addEventListener("click", (event: MouseEvent) => {
    if (onConfirm) {
      onConfirm(event, datasetStrings);
    }
    closeDialog("confirm");
  });

  dialog.addEventListener("close", () => {
    body.removeChild(dialog);
    if (previouslyFocusedElement) {
      previouslyFocusedElement.focus();
    }
    unlockBodyScroll();
  });

  lockBodyScroll();
  dialog.showModal();
}

function getRequiredElement(root: HTMLElement, selector: string): HTMLElement {
  const element = root.querySelector(selector);
  if (!(element instanceof HTMLElement)) {
    throw new InternalOnlyError(`Alert dialog is missing its "${selector}" element.`);
  }
  return element;
}

function getRequiredButton(root: HTMLElement, selector: string): HTMLButtonElement {
  const element = root.querySelector(selector);
  if (!(element instanceof HTMLButtonElement)) {
    throw new InternalOnlyError(`Alert dialog is missing its "${selector}" button.`);
  }
  return element;
}

function validateDataAttributeIsSet(attribute: ?string, error: string) {
  if (attribute == null) {
    throw new Error(error);
  }
}

// If the button has a data-on-button-click-is-armed-fn attribute, that function must return true
// for the dialog to be shown. When it returns false, the click handler fires directly with the
// original event and no confirmation dialog is shown. Returns true if the dialog was bypassed.
function bypassDialogIfNotArmed(
  event: Event,
  armedFnName: ?string,
  onConfirm: ?(event: Event, datasetRest: { [string]: string }) => void,
  datasetRest: { [string]: string }
): boolean {
  const armedFn = armedFnName ? window[armedFnName] : undefined;
  if (armedFn != null && !armedFn(event)) {
    if (onConfirm) {
      onConfirm(event, datasetRest);
    }
    return true;
  }
  return false;
}

export function bindDestroyAlertButton(button: HTMLElement) {
  button.addEventListener("click", (event: Event) => {
    event.preventDefault();
    const {
      title,
      message,
      destroyButtonLabel,
      destroyButtonDisabled,
      onDestroyClickedFn,
      onButtonClickIsArmedFn,
      againstConventionsCancelButtonLabel,
      disableCloseOnUnderlayClick,
      ...datasetRest
    } = button.dataset;

    validateDataAttributeIsSet(title, "Every destroy dialog must have a 'title'.");
    validateDataAttributeIsSet(message, "Every destroy dialog must have a 'message'.");
    validateDataAttributeIsSet(destroyButtonLabel, "Every destroy dialog must have a 'destroyButtonLabel'.");
    validateDataAttributeIsSet(onDestroyClickedFn, "Every destroy dialog must have an 'onDestroyClickedFn'.");

    const onConfirm = onDestroyClickedFn ? window[onDestroyClickedFn] : undefined;
    if (bypassDialogIfNotArmed(event, onButtonClickIsArmedFn, onConfirm, datasetRest)) {
      return;
    }
    showAlertDialog({
      ...datasetRest,
      title,
      message,
      confirmButtonLabel: destroyButtonLabel,
      confirmButtonDisabled: destroyButtonDisabled != null,
      onConfirm,
      cancelButtonLabel: againstConventionsCancelButtonLabel,
      isDestructive: true,
      disableCloseOnUnderlayClick: disableCloseOnUnderlayClick != null,
    });
  });
}

export function bindWarningAlertButton(button: HTMLElement) {
  button.addEventListener("click", (event: Event) => {
    event.preventDefault();
    const {
      title,
      message,
      warningButtonLabel,
      warningButtonDisabled,
      onWarningClickedFn,
      onButtonClickIsArmedFn,
      againstConventionsCancelButtonLabel,
      disableCloseOnUnderlayClick,
      ...datasetRest
    } = button.dataset;

    validateDataAttributeIsSet(title, "Every warning dialog must have a 'title'");
    validateDataAttributeIsSet(message, "Every warning dialog must have a 'message'.");
    validateDataAttributeIsSet(warningButtonLabel, "Every warning dialog must have a 'warningButtonLabel'.");
    validateDataAttributeIsSet(onWarningClickedFn, "Every warning dialog must have an 'onWarningClickedFn'.");

    const onConfirm = onWarningClickedFn ? window[onWarningClickedFn] : undefined;
    if (bypassDialogIfNotArmed(event, onButtonClickIsArmedFn, onConfirm, datasetRest)) {
      return;
    }
    showAlertDialog({
      ...datasetRest,
      title,
      message,
      confirmButtonLabel: warningButtonLabel,
      confirmButtonDisabled: warningButtonDisabled != null,
      onConfirm,
      cancelButtonLabel: againstConventionsCancelButtonLabel,
      isDestructive: false,
      disableCloseOnUnderlayClick: disableCloseOnUnderlayClick != null,
    });
  });
}

document.addEventListener("DOMContentLoaded", () => {
  for (const button of document.querySelectorAll("[data-show-confirm-destroy-alert-on-click]")) {
    if (button instanceof HTMLElement) {
      bindDestroyAlertButton(button);
    }
  }
  for (const button of document.querySelectorAll("[data-show-confirm-warning-alert-on-click]")) {
    if (button instanceof HTMLElement) {
      bindWarningAlertButton(button);
    }
  }
});
